// Taşeron portalı — Araçlar (api-contract §23.2 `GET /supplier/contractor/vehicles`). Araç kaydı
// firma düzeyindedir; izinler ona bağlanır, kopyalamaz. Sheet ve yükleme hedefi SATIR DEĞİL uuid
// saklar: her render'da güncel sorgu verisinden bulunur, aksi hâlde belge listesi mutasyondan
// sonra tazelenmez. Form çekmecesi yalnız bu sayfanın denetimlerinden açılır.

import {
  Breadcrumb,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  type ContractorVehicleDto,
  EmptyState,
  PageHeader,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  VehicleDocStatus,
  VehicleFormSheet,
  type VehicleFormTarget,
  VehicleSheet,
  canEditPlate,
  supplierContractorApi,
  useAuthStore,
  useRowRemovalFocus,
  vehicleDeleteBlock,
  vehicleDeleteLabelKey,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Truck } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContractorStateNotice } from '../components/ContractorStateNotice';
import { VehicleDocumentUploadModal } from '../components/vehicles/VehicleDocumentUploadModal';
import { VehicleDocuments } from '../components/vehicles/VehicleDocuments';
import { supplierContractorKeys } from '../lib/supplierContractorKeys';
import { useTableDensity } from '../lib/uiPrefs';
import { useReturnFocus } from '../lib/useReturnFocus';
import { invalidateVehicleCaches } from '../lib/vehicleCache';
import { useVehicleClient } from '../lib/vehicleClient';

const COLUMN_COUNT = 6;

export function VehiclesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);
  const vehicleClient = useVehicleClient();
  const query = useQuery({
    queryKey: supplierContractorKeys.vehicles(companyId),
    queryFn: () => supplierContractorApi.listSupplierVehicles(),
  });
  const rows = query.data ?? [];
  const [density, setDensity] = useTableDensity();

  const [formTarget, setFormTarget] = useState<VehicleFormTarget | null>(null);
  const [sheetUuid, setSheetUuid] = useState<string | null>(null);
  const [uploadUuid, setUploadUuid] = useState<string | null>(null);
  // Yükleme penceresi oturumu: hedef her null→uuid (ya da uuid→başka uuid) geçişinde artar;
  // modal `key` olarak alır → yeni açılış taze durumla başlar, kapanış AYNI örnekte kalır
  // (kapanış animasyonu, odak dönüşü, uçuştaki yüklemenin onSuccess/onError'ı korunur).
  const [uploadPrev, setUploadPrev] = useState<string | null>(null);
  const [uploadSession, setUploadSession] = useState(0);
  if (uploadUuid !== uploadPrev) {
    setUploadPrev(uploadUuid);
    if (uploadUuid !== null) {
      setUploadSession((n) => n + 1);
    }
  }
  const [toDelete, setToDelete] = useState<ContractorVehicleDto | null>(null);
  const addTrigger = useReturnFocus();
  const plateOpenerRef = useRef<HTMLElement | null>(null);
  const formRestoreRef = useRef<HTMLElement | null>(null);
  const plateButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const rowKeys = useMemo(() => rows.map((v) => v.uuid), [rows]);

  const sheetVehicle = rows.find((v) => v.uuid === sheetUuid) ?? null;
  const uploadVehicle = rows.find((v) => v.uuid === uploadUuid) ?? null;

  // Hedef araç listeden kalkınca saklanan uuid bayatlamasın: odak efekti örtü açık sanmasın ve
  // sonradan yeniden bağlanan/eklenen aynı araç eski durumla kendiliğinden açılmasın.
  useEffect(() => {
    if (sheetUuid !== null && sheetVehicle === null) {
      setSheetUuid(null);
    }
    if (uploadUuid !== null && uploadVehicle === null) {
      setUploadUuid(null);
    }
  }, [sheetUuid, sheetVehicle, uploadUuid, uploadVehicle]);

  // Satır menüsünden açılan modallar (yükle / sil) odağı o satırın plaka düğmesine döndürür; silinen
  // satır listeden kalkınca ve diyalog kapanınca odak "Araç ekle"ye iner (ortak durum makinesi).
  const focus = useRowRemovalFocus({
    rowKeys,
    stamps: { dataUpdatedAt: query.dataUpdatedAt, errorUpdatedAt: query.errorUpdatedAt },
    addButtonRef: addTrigger.ref,
    dialogOpen: toDelete !== null,
    otherSurfaceOpen: formTarget !== null || sheetVehicle !== null || uploadVehicle !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: (vehicleUuid: string) => supplierContractorApi.deleteSupplierVehicle(vehicleUuid),
    onSuccess: (_data, vehicleUuid) => {
      invalidateVehicleCaches(queryClient, companyId);
      // Araç sunucuda silindi → diyaloğun kapanış dönüşü artık "Araç ekle"ye iner.
      focus.removed(vehicleUuid);
      setToDelete(null);
    },
    // 409 VEHICLE_HAS_OPEN_PERMITS / VEHICLE_HAS_LOCKED_DOCUMENTS: bayat olgu → tazele; satır yenilemede kalkmış olabilir.
    onError: (_error, vehicleUuid) => {
      invalidateVehicleCaches(queryClient, companyId);
      focus.removeFailed(vehicleUuid);
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb items={[{ label: t('vehicles.title') }]} />
      <PageHeader
        title={t('vehicles.title')}
        helperText={t('vehicles.subtitle')}
        actions={
          query.isSuccess ? (
            <Button
              ref={addTrigger.ref}
              onClick={() => {
                formRestoreRef.current = addTrigger.ref.current;
                setFormTarget({ mode: 'create' });
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('vehicles.add')}
            </Button>
          ) : undefined
        }
      />

      {query.isError ? (
        <ContractorStateNotice error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <Card className="carbon:p-0">
          <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
            {/* Uç noktada arama/sayfalama YOK (dizi döner) → araç çubuğu yalnız özet sayı. */}
            <TableToolbar
              summary={query.data ? t('vehicles.list.count', { count: rows.length }) : undefined}
              density={density}
              onDensityChange={setDensity}
              densityLabel={t('common.compactRows')}
            />
            {/* `relative`: sütun başlığındaki `sr-only` metnin kapsayıcı bloğu tablo olsun — aksi
                hâlde taşan tabloda sağ kenardaki mutlak konumlu metin kaydırıcıdan kaçar ve sayfayı
                yatay kaydırır. Açıklama sütunu sıkışmayı emer (≥sm `w-full` + `max-w-0`: içerik
                genişliği tabloya katkı vermez, sütun kalan genişliği alır ve kırpılır); <sm tablo
                zaten kendi kabında kayar → sabit 12rem. */}
            <Table caption={t('vehicles.title')} density={density} className="relative">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('vehicles.col.plate')}</TableHead>
                  <TableHead className="w-full truncate sm:max-w-0">
                    {t('vehicles.col.description')}
                  </TableHead>
                  <TableHead className="text-right">{t('vehicles.col.documents')}</TableHead>
                  <TableHead>{t('vehicles.col.docStatus')}</TableHead>
                  <TableHead>{t('vehicles.col.openPermits')}</TableHead>
                  <TableHead className="w-12">
                    <span className="sr-only">{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody aria-busy={query.isPending || undefined}>
                {query.isPending ? (
                  <TableSkeletonRows columns={COLUMN_COUNT} />
                ) : rows.length === 0 ? (
                  <TableMessageRow colSpan={COLUMN_COUNT}>
                    <EmptyState icon={Truck} title={t('vehicles.empty')} />
                  </TableMessageRow>
                ) : (
                  rows.map((vehicle) => (
                    <TableRow key={vehicle.uuid}>
                      <TableCell truncate={false}>
                        <div className="max-w-[8rem]">
                          <button
                            type="button"
                            ref={(el) => {
                              if (el) {
                                plateButtonRefs.current.set(vehicle.uuid, el);
                              } else {
                                plateButtonRefs.current.delete(vehicle.uuid);
                              }
                            }}
                            title={vehicle.plate}
                            onClick={(e) => {
                              plateOpenerRef.current = e.currentTarget;
                              setSheetUuid(vehicle.uuid);
                            }}
                            className="block w-full truncate text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {vehicle.plate}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell truncate={false} className="text-muted sm:max-w-0">
                        <span
                          className="block w-48 truncate sm:w-auto"
                          title={vehicle.description ?? undefined}
                        >
                          {vehicle.description ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell className="tnum text-right">{vehicle.documents.length}</TableCell>
                      <TableCell>
                        <VehicleDocStatus documents={vehicle.documents} actor="contractor" />
                      </TableCell>
                      <TableCell>
                        {vehicle.hasOpenPermits ? (
                          <span className="text-sm text-muted">
                            {t('vehicles.openPermits.yes')}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell className="w-12 px-2">
                        <RowActionsMenu
                          ariaLabel={t('vehicles.action.openMenu', { plate: vehicle.plate })}
                          items={[
                            {
                              key: 'upload',
                              label: t('vehicles.action.upload'),
                              onSelect: () => {
                                focus.rememberOpener(
                                  plateButtonRefs.current.get(vehicle.uuid) ?? null,
                                );
                                setUploadUuid(vehicle.uuid);
                              },
                            },
                            {
                              key: 'edit',
                              label: t('vehicles.action.edit'),
                              onSelect: () => {
                                formRestoreRef.current =
                                  plateButtonRefs.current.get(vehicle.uuid) ?? null;
                                setFormTarget({
                                  mode: 'edit',
                                  vehicle,
                                  plateLocked: !canEditPlate(vehicle, 'contractor'),
                                });
                              },
                            },
                            {
                              key: 'delete',
                              destructive: true,
                              // Devre dışı öğe nedenini etiketinde taşır; "Açık izin" sütunu klavye için tekrarlar.
                              label: t(
                                vehicleDeleteLabelKey(
                                  vehicleDeleteBlock(vehicle, 'contractor'),
                                  'contractor',
                                ),
                              ),
                              disabled: vehicleDeleteBlock(vehicle, 'contractor') !== null,
                              onSelect: () => {
                                deleteMutation.reset();
                                focus.rememberOpener(
                                  plateButtonRefs.current.get(vehicle.uuid) ?? null,
                                );
                                setToDelete(vehicle);
                              },
                            },
                          ]}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <VehicleSheet
        open={sheetVehicle !== null}
        onOpenChange={(open) => !open && setSheetUuid(null)}
        actor="contractor"
        context={sheetVehicle ? { kind: 'registry', vehicle: sheetVehicle } : null}
        restoreFocusRef={plateOpenerRef}
        documents={
          sheetVehicle ? <VehicleDocuments companyId={companyId} vehicle={sheetVehicle} /> : null
        }
      />

      {formTarget ? (
        <VehicleFormSheet
          key={formTarget.mode === 'edit' ? formTarget.vehicle.uuid : 'create'}
          client={vehicleClient}
          target={formTarget}
          onClose={() => setFormTarget(null)}
          restoreFocusRef={formRestoreRef}
        />
      ) : null}

      <VehicleDocumentUploadModal
        // Her yeni açılışta dosya/tür/not/hata durumu sıfırlansın (hedef kalkıp dönse bile).
        key={uploadSession}
        open={uploadVehicle !== null}
        onOpenChange={(open) => !open && setUploadUuid(null)}
        companyId={companyId}
        vehicleUuid={uploadUuid ?? ''}
        vehiclePlate={uploadVehicle?.plate ?? ''}
        restoreFocusRef={focus.menuRestoreRef}
      />

      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => !open && setToDelete(null)}
        title={t('vehicles.delete.title')}
        body={t('vehicles.delete.body', { plate: toDelete?.plate ?? '' })}
        confirmLabel={t('vehicles.delete.confirm')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
